/**
 * Interval poller for the live dashboards, extracted from the pattern in
 * Dashboards/PlantLiveStatus/PlantLiveStatus.jsx:
 *
 *   - never starts a refresh while the previous one is still in flight, so a slow
 *     server does not get a pile-up of overlapping requests;
 *   - skips ticks while the tab is hidden (wall TVs that switch input, background
 *     tabs), and refreshes once on return if a refresh is overdue;
 *   - stop() clears both the interval and the visibility listener, so unmounting
 *     really stops the traffic;
 *   - run receives { reportError }, which calls notifyError (e.g. swal) once per
 *     outage: the first failure notifies, later ones stay quiet until a refresh
 *     completes without errors. Wall-mounted dashboards no longer pile up popups.
 *
 * Works from class components (create in componentDidMount, stop in
 * componentWillUnmount) and hooks (create and start inside useEffect, return stop).
 *
 *   this.poller = createLivePoller({
 *     intervalMs: RefreshRate(company),
 *     notifyError: swal,
 *     run: async ({ reportError }) => { try { ... } catch (err) { reportError(`${err}`); } },
 *   });
 *   this.poller.start();
 *   ...
 *   this.poller.stop();
 */
export function createLivePoller({ intervalMs, run, notifyError }) {
  let timer = null;
  let running = false;
  let inFlight = false;
  let lastRunAt = 0;
  let errorShown = false;

  const isHidden = () => typeof document !== "undefined" && document.hidden;

  const tick = async () => {
    if (!running || inFlight || isHidden()) return;
    inFlight = true;
    let tickFailed = false;
    const reportError = (...args) => {
      tickFailed = true;
      if (errorShown) return;
      errorShown = true;
      if (typeof notifyError === "function") notifyError(...args);
    };
    try {
      await run({ reportError });
    } catch (e) {
      // run() owns its error handling; a throw here must not kill the poller.
      tickFailed = true;
    } finally {
      if (!tickFailed) errorShown = false;
      lastRunAt = Date.now();
      inFlight = false;
    }
  };

  const onVisibilityChange = () => {
    if (!isHidden() && Date.now() - lastRunAt >= intervalMs) tick();
  };

  return {
    /** Starts ticking. Pass { immediate: true } to also run once right away. */
    start({ immediate = false } = {}) {
      if (running) return;
      running = true;
      lastRunAt = Date.now();
      timer = setInterval(tick, intervalMs);
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibilityChange);
      }
      if (immediate) tick();
    },

    stop() {
      running = false;
      if (timer) clearInterval(timer);
      timer = null;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    },

    /** Runs a refresh now (still respecting the in-flight guard). */
    refreshNow: tick,

    isRunning: () => running,
  };
}

export default createLivePoller;
